import type { Tokens } from './schemas'

import * as v from 'valibot'
import {
  formatDateTimeWithYearUnified,
  formatExpirationDate,
  hasISO8601TimezoneDefined,
} from '~/shared/helpers/date'
import { TokensSchema } from './schemas'

/**
 * Replaces tokens inside text to their values
 *
 * @param {string} text - text, containing tokens, like
 *                 $firstName, $lastName and so on
 * @param {object} tokens - map with tokens and values, like
 *                 { '$firstName': 'Victor', '$lastName': 'Didenko' }
 */
export function replaceTokens(text: string, tokens?: Tokens) {
  // empty text, nothing to replace
  // no tokens provided, nothing to replace
  // tokens is not in correct format, nothing to replace
  if (!text || !tokens || !v.is(TokensSchema, tokens)) {
    return text
  }

  // get all tokens
  const keys = Object.keys(tokens)

  // tokens object is empty, nothing to replace
  if (keys.length === 0) {
    return text
  }

  // replace tokens in text with their values
  const re = keys
    .sort((a, b) => b.length - a.length) // sort tokens by length in descending order, to replace longer tokens first
    .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // escape special characters in tokens for regex
    .join('|')
  const regex = new RegExp(re, 'g')
  return text.replace(regex, (match) => tokens[match])
}

/**
 * Format expiration date token to human-readable format
 */
export function formatTokens(tokens?: Tokens): Tokens | undefined {
  if (tokens && tokens.$expirationDate) {
    return {
      ...tokens,
      $expirationDate: formatExpirationDate(tokens.$expirationDate),
    }
  }

  return tokens
}

function formatStoredIsoDateTime(iso: string): string {
  const withTz = hasISO8601TimezoneDefined(iso) ? iso : `${iso}Z`
  return formatDateTimeWithYearUnified(withTz)
}

/** Replace raw ISO timestamps in announcement copy with the unified client format. */
export function formatIsoTimestampsInText(text: string): string {
  if (!text) return text

  return text
    .replace(
      /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g,
      formatStoredIsoDateTime
    )
    .replace(/\d{4}-\d{2}-\d{2}(?!T)/g, (iso) =>
      formatExpirationDate(`${iso}T00:00:00Z`)
    )
}

export function formatAnnouncementText(text: string, tokens?: Tokens): string {
  return formatIsoTimestampsInText(replaceTokens(text, formatTokens(tokens)))
}
